import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

interface Props {
  onExtend: () => Promise<void>;
  onLogout: () => void;
  onExpire: () => void;
}

export default function SessionWarningModal({ onExtend, onLogout, onExpire }: Props) {
  const [secondsLeft, setSecondsLeft] = useState(300);
  const [extending, setExtending] = useState(false);
  const onExpireRef = useRef(onExpire);
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(id);
          onExpireRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const countdown = `${mins}:${String(secs).padStart(2, '0')}`;
  const urgent = secondsLeft <= 60;

  const handleExtend = async () => {
    setExtending(true);
    try {
      await onExtend();
    } finally {
      setExtending(false);
    }
  };

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onMouseDown={e => e.stopPropagation()}
        style={{
          background: 'rgba(20,30,50,0.97)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 20,
          padding: 28,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 8, flexShrink: 0,
            background: urgent ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>
            ⏱
          </div>
          <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.0625rem', fontWeight: 600 }}>
            Session Expiring Soon
          </h3>
        </div>

        {/* Countdown block */}
        <div style={{
          textAlign: 'center',
          padding: '18px 0',
          marginBottom: 20,
          borderTop: '1px solid rgba(255,255,255,0.07)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div style={{
            fontSize: '2.75rem',
            fontWeight: 700,
            lineHeight: 1,
            color: urgent ? '#ef4444' : '#f59e0b',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.04em',
          }}>
            {countdown}
          </div>
          <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: 10, lineHeight: 1.5 }}>
            Your session will expire in {countdown}. Any unsaved work will be lost.
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onLogout}
            style={{
              flex: 1,
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'transparent',
              color: '#9ca3af',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = '#252d3d';
              (e.currentTarget as HTMLButtonElement).style.color = '#f9fafb';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = '#9ca3af';
            }}
          >
            Log Out Now
          </button>
          <button
            onClick={handleExtend}
            disabled={extending}
            style={{
              flex: 2,
              padding: '9px 16px',
              borderRadius: 8,
              border: 'none',
              background: extending ? 'rgba(59,130,246,0.55)' : '#3b82f6',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: extending ? 'default' : 'pointer',
              transition: 'background 150ms ease',
            }}
          >
            {extending ? 'Refreshing…' : 'Stay Logged In'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
